import { put, select, takeLatest } from 'redux-saga/effects'
import { callApi } from '@/shared/tools/apiSagaHelperV2'
import {
  initialize,
  setProvider,
  setComparators,
  addComparator,
  saving,
  removeProvider,
  updateProvider,
  closeOptInSms,
  savingOptInSms,
} from './organizationsManagementSlice'
import {
  selectOrganizationsOptInSms,
  selectOrganizationsRoutes,
  selectOrganizationsSelectedProvider,
} from './organizationsManagementSelectors'
import { selectExtraRoutesAuthData } from '../Login/AuthSelectors'

export const organizationsManagementActions = {
  INITIALIZE: 'ORGANIZATIONS/MANAGEMENT/INITIALIZE',
  LOAD_PROVIDER: 'ORGANIZATIONS/MANAGEMENT/LOAD_PROVIDER',
  LOAD_COMPARATORS: 'ORGANIZATIONS/MANAGEMENT/LOAD_COMPARATORS',
  SAVE_COMPARATOR: 'ORGANIZATIONS/MANAGEMENT/SAVE_COMPARATOR',
  DELETE_PROVIDER: 'ORGANIZATIONS/MANAGEMENT/DELETE_PROVIDER',
  SAVE_OPT_IN_SMS_TEXT: 'ORGANIZATIONS/MANAGEMENT/SAVE_OPT_IN_SMS_TEXT',
}

function* initializeOrganizationsManagementSaga({ enqueue, url }) {
  try {
    if (!url) {
      return
    }

    const data = yield callApi({
      endpoint: url,
      method: 'GET',
      enqueue,
      showErrorSnackbar: false,
    })

    if (data) {
      yield put(initialize(data))
    }
  } catch (error) {
    // Silently fail initialization - the component will show fallback
  }
}

function* loadProviderSaga({ providerId }) {
  try {
    const routes = yield select(selectOrganizationsRoutes)

    if (!routes.providerInformation || !providerId) {
      return
    }

    const data = yield callApi({
      endpoint: routes.providerInformation,
      method: 'GET',
      asteriskReplacement: providerId,
      showErrorSnackbar: false,
    })

    if (data) {
      yield put(setProvider(data))
    }
  } catch (error) {
    // Silently fail - component will handle empty state
  }
}

function* loadComparatorsSaga() {
  try {
    const { externalPartnersListUrl } = yield select(selectExtraRoutesAuthData)
    if (!externalPartnersListUrl) {
      return
    }

    const data = yield callApi({
      endpoint: externalPartnersListUrl,
      method: 'GET',
      showErrorSnackbar: false,
    })

    if (data) {
      yield put(setComparators(data))
    }
  } catch (error) {
    // Silently fail
  }
}

function* saveComparatorSaga({ name, enqueue, onSuccess, onError }) {
  try {
    const { externalPartnersStoreUrl } = yield select(selectExtraRoutesAuthData)

    if (!externalPartnersStoreUrl || !name) {
      return
    }

    yield put(saving({ loading: true }))

    const data = yield callApi({
      endpoint: externalPartnersStoreUrl,
      method: 'POST',
      payload: { name },
      enqueue,
      showErrorSnackbar: true,
    })

    if (data) {
      yield put(addComparator(data))
      if (onSuccess) {
        onSuccess()
      }
    }

    yield put(saving({ loading: false }))
  } catch (error) {
    yield put(saving({ loading: false }))
    if (onError) {
      onError(error)
    }
  }
}

function* deleteProviderSaga({ providerId, enqueue, onSuccess, onError }) {
  try {
    const routes = yield select(selectOrganizationsRoutes)

    const deleteRoute = routes.delete
    if (!deleteRoute || !providerId) {
      return
    }

    yield put(saving({ loading: true }))

    yield callApi({
      endpoint: deleteRoute,
      method: 'DELETE',
      asteriskReplacement: providerId,
      enqueue,
      showErrorSnackbar: true,
    })

    yield put(removeProvider(providerId))
    if (onSuccess) {
      onSuccess()
    }

    yield put(saving({ loading: false }))
  } catch (error) {
    yield put(saving({ loading: false }))
    if (onError) {
      onError(error)
    }
  }
}

export function* saveOptInSmsTextSaga({ enqueue }) {
  const routes = yield select(selectOrganizationsRoutes)
  const { uuid } = yield select(selectOrganizationsSelectedProvider)
  const { draft } = yield select(selectOrganizationsOptInSms)
  if (!routes.optInSmsText || !uuid) {
    return
  }

  yield put(savingOptInSms(true))
  try {
    const data = yield callApi({
      endpoint: routes.optInSmsText,
      method: 'PUT',
      asteriskReplacement: uuid,
      payload: { opt_in_sms_text: draft },
      enqueue,
      showErrorSnackbar: true,
    })
    yield put(updateProvider({ opt_in_sms_text: data?.opt_in_sms_text ?? null }))
    yield put(closeOptInSms())
  } catch {
    yield put(savingOptInSms(false))
  }
}

export default function* organizationsManagementSagas() {
  yield takeLatest(organizationsManagementActions.INITIALIZE, initializeOrganizationsManagementSaga)
  yield takeLatest(organizationsManagementActions.LOAD_PROVIDER, loadProviderSaga)
  yield takeLatest(organizationsManagementActions.LOAD_COMPARATORS, loadComparatorsSaga)
  yield takeLatest(organizationsManagementActions.SAVE_COMPARATOR, saveComparatorSaga)
  yield takeLatest(organizationsManagementActions.DELETE_PROVIDER, deleteProviderSaga)
  yield takeLatest(organizationsManagementActions.SAVE_OPT_IN_SMS_TEXT, saveOptInSmsTextSaga)
}
